import { of as observableOf, Observable } from 'rxjs';

import { tap, switchMap, map, withLatestFrom, mergeMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { LoopBackAuth, MemberApi, OrganisationsApi } from '@careapp/angular-sdk';
import { Actions, Effect } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { CAAction } from '../actions';
import { Staff } from '../actions/index';
import * as selectors from '../reducers/index';

@Injectable()
export class StaffEffects {
  @Effect()
  paginate = this.actions$.ofType(Staff.PAGE_CHANGE).pipe(map(action => Staff.RequestAllAction));

  @Effect()
  updateFilter = this.actions$
    .ofType(Staff.UPDATE_FILTER)
    .pipe(map(action => Staff.RequestPageAction()));

  @Effect({ dispatch: false })
  updatePagination = this.actions$.ofType(Staff.RESET_PAGE, Staff.REQUEST_PAGE).pipe(
    tap(action => {
      this.store.dispatch(Staff.RequestCountAction());
      this.store.dispatch(Staff.RequestAllAction);
    })
  );

  @Effect()
  getStaff = this.actions$.ofType(Staff.REQUEST_ALL).pipe(
    withLatestFrom(
      this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
      this.store.select(selectors.getPageination(selectors.STAFF)),
      this.store.select(selectors.getFilter(selectors.STAFF))
    ),
    switchMap(([action, organisationId, pagination, filter]) => {
      return this.api
        .listMembers(organisationId, {
          ...filter,
          limit: pagination.pageSize,
          skip: pagination.pageSize * pagination.pageIndex
        })
        .map((payload: any) => {
          return Staff.ResponseSuccessAllAction(payload);
        })
        .catch(err => observableOf(Staff.ResponseFailAllAction(err)));
    })
  );

  @Effect()
  searchStaff = this.actions$.ofType(Staff.REQUEST_SEARCH).pipe(
    withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
    switchMap(([action, organisationId]) =>
      this.api
        .listMembers(organisationId, action.payload)
        .map(result => Staff.ResponseSuccessSearchAction(result))
        .catch(err => observableOf(Staff.ResponseFailSearchAction(err)))
    )
  );

  @Effect()
  countStaff = this.actions$.ofType(Staff.REQUEST_COUNT).pipe(
    withLatestFrom(
      this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
      this.store.select(selectors.getPageination(selectors.STAFF))
    ),
    switchMap(([action, organisationId, pagination]) => {
      return this.api
        .countMembers(organisationId, {})
        .map((payload: any) => {
          return Staff.ResponseSuccessCountAction(payload.count);
        })
        .catch(err => observableOf(Staff.ResponseFailCountAction(err)));
    })
  );

  @Effect()
  staffById = this.actions$.ofType(Staff.REQUEST_BY_ID).pipe(
    withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
    mergeMap(([action, organisationId]) => {
      return this.members
        .profile(action.payload.id, headers => {
          headers = headers.append('Organisation-Id', organisationId);
          return headers;
        })
        .map((payload: any) => {
          return Staff.ResponseSuccessByIdAction(payload);
        })
        .catch(err => {
          return observableOf(Staff.ResponseFailByIdAction(err));
        });
    })
  );

  @Effect()
  updateRole = this.actions$.ofType(Staff.REQUEST_UPDATE_ROLE).pipe(
    withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
    switchMap(([action, organisationId]) => {
      return this.api
        .updateOrganisationRole(organisationId, action.payload.id, action.payload.role)
        .map(payload => Staff.ResponseSuccessUpdateRole(payload))
        .catch(err => observableOf(Staff.ResponseFailUpdateRole(err)));
    })
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private api: OrganisationsApi,
    private members: MemberApi,
    private auth: LoopBackAuth
  ) {}
}
